/**
 * RS / Sign Up — sumi-e ink brush, interaction-driven.
 *
 * The prototype recomposed the whole canvas every frame forever. Here the
 * rAF loop exists only while there is wet ink: it wakes on pointer/touch
 * movement (dabs onto a persistent offscreen layer), keeps drying the stain
 * for a bounded time after the last stroke, then clears the layer and stops.
 * Nothing runs on an idle page.
 *
 * Safety rails: IntersectionObserver (never active out of viewport),
 * a runtime FPS guard —
 * the first active frames are timed on the real device; too slow → the
 * effect shuts off for good and the page stays static.
 */
const DRY_AFTER_MS = 9000;
const GUARD_FRAMES = 40;
const GUARD_BUDGET_MS = 24;

class RsSignup extends HTMLElement {
  #raf = null;
  #visible = false;
  #dead = false;
  #lastStroke = 0;
  #lastFrame = 0;
  #guardTotal = 0;
  #guardCount = 0;

  connectedCallback() {
    this.canvas = this.querySelector('[data-rs-ink]');
    if (!this.canvas) return;
    if (this.dataset.ink !== 'true') return;

    this.strength = parseFloat(this.dataset.inkStrength) || 5;
    this.ctx = this.canvas.getContext('2d');
    this.ink = document.createElement('canvas');
    this.ictx = this.ink.getContext('2d');
    this.dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.brushR = 26;
    this.ptr = { x: -9999, y: -9999, px: -9999, py: -9999, active: false };

    this.#resize();
    this.onResize = () => this.#resize();
    window.addEventListener('resize', this.onResize);
    // Pointer handlers fire at high rate: the rect is cached and refreshed
    // only when scroll/resize can move it
    this.onScroll = () => (this.rectDirty = true);
    window.addEventListener('scroll', this.onScroll, { passive: true });

    this.onMove = (event) => this.#setPointer(event.clientX, event.clientY);
    this.onTouch = (event) => {
      if (event.touches && event.touches[0]) {
        this.#setPointer(event.touches[0].clientX, event.touches[0].clientY);
      }
    };
    this.onLeave = () => {
      this.ptr.active = false;
      this.ptr.x = -9999;
      this.ptr.y = -9999;
    };
    this.addEventListener('mousemove', this.onMove, { passive: true });
    this.addEventListener('touchmove', this.onTouch, { passive: true });
    this.addEventListener('touchstart', this.onTouch, { passive: true });
    this.addEventListener('mouseleave', this.onLeave);
    this.addEventListener('touchend', this.onLeave);

    this.observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        this.#visible = entry.isIntersecting;
        if (!this.#visible) this.#stop();
        else if (performance.now() - this.#lastStroke < DRY_AFTER_MS) this.#start();
      }
    });
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.#stop();
    this.observer?.disconnect();
    window.removeEventListener('resize', this.onResize);
    window.removeEventListener('scroll', this.onScroll);
  }

  #rect() {
    if (!this.cachedRect || this.rectDirty) {
      this.cachedRect = this.getBoundingClientRect();
      this.rectDirty = false;
    }
    return this.cachedRect;
  }

  #resize() {
    this.rectDirty = true;
    this.W = this.clientWidth;
    this.H = this.clientHeight;
    this.canvas.width = this.W * this.dpr;
    this.canvas.height = this.H * this.dpr;
    this.ink.width = this.W * this.dpr;
    this.ink.height = this.H * this.dpr;
    this.ictx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
    this.ctx.setTransform(1, 0, 0, 1, 0, 0);
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
  }

  #setPointer(clientX, clientY) {
    if (this.#dead) return;
    const box = this.#rect();
    const x = clientX - box.left;
    const y = clientY - box.top;
    this.ptr.px = this.ptr.active ? this.ptr.x : x;
    this.ptr.py = this.ptr.active ? this.ptr.y : y;
    this.ptr.x = x;
    this.ptr.y = y;
    this.ptr.active = true;
    this.#lastStroke = performance.now();
    if (this.#visible) this.#start();
  }

  #dab(x, y, dirX, dirY, speed) {
    const ictx = this.ictx;
    const r = this.brushR * (0.7 + Math.random() * 0.5);
    const a = 0.012 + Math.min(speed * 0.0015, 0.02) * (this.strength / 5);
    const g = ictx.createRadialGradient(x, y, 0, x, y, r);
    g.addColorStop(0, `rgba(26,26,29,${a.toFixed(4)})`);
    g.addColorStop(0.7, `rgba(20,20,23,${(a * 0.5).toFixed(4)})`);
    g.addColorStop(1, 'rgba(16,16,18,0)');
    const ang = Math.atan2(dirY, dirX);
    ictx.beginPath();
    ictx.ellipse(x + (Math.random() - 0.5) * 4, y + (Math.random() - 0.5) * 4, r * 1.15, r * 0.8, ang, 0, Math.PI * 2);
    ictx.fillStyle = g;
    ictx.fill();
    if (Math.random() < 0.12) {
      const fa = ang + (Math.random() - 0.5) * 1.2;
      const fd = r * (1 + Math.random());
      ictx.beginPath();
      ictx.arc(x + Math.cos(fa) * fd, y + Math.sin(fa) * fd, 1 + Math.random() * 2.5, 0, Math.PI * 2);
      ictx.fillStyle = `rgba(24,24,27,${(a * 1.4).toFixed(4)})`;
      ictx.fill();
    }
  }

  #start() {
    if (this.#raf || this.#dead) return;
    this.#lastFrame = performance.now();
    this.#raf = requestAnimationFrame(this.#tick);
  }

  #stop() {
    cancelAnimationFrame(this.#raf);
    this.#raf = null;
  }

  #tick = (now) => {
    const frameStart = performance.now();
    const dt = Math.min((now - this.#lastFrame) / 16.7, 3);
    this.#lastFrame = now;
    const ptr = this.ptr;

    if (ptr.active) {
      const pdx = ptr.x - ptr.px;
      const pdy = ptr.y - ptr.py;
      const speed = Math.hypot(pdx, pdy);
      if (speed > 0.3) {
        this.brushR += ((speed < 6 ? 34 : 18) - this.brushR) * 0.08 * dt;
        const steps = Math.max(1, Math.round(speed / 6));
        for (let i = 0; i < steps; i++) {
          const t = i / steps;
          this.#dab(ptr.px + pdx * t, ptr.py + pdy * t, pdx, pdy, speed);
        }
      }
      ptr.px = ptr.x;
      ptr.py = ptr.y;
    }

    // The stain dries slowly back into the black
    this.ictx.globalCompositeOperation = 'destination-out';
    this.ictx.fillStyle = 'rgba(0,0,0,0.006)';
    this.ictx.fillRect(0, 0, this.W, this.H);
    this.ictx.globalCompositeOperation = 'source-over';

    // Composite ink over the transparent canvas (page background shows through)
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    this.ctx.drawImage(this.ink, 0, 0);

    // Runtime FPS guard: the device measures itself; too slow → static page
    if (this.#guardCount < GUARD_FRAMES) {
      this.#guardTotal += performance.now() - frameStart;
      this.#guardCount += 1;
      if (this.#guardCount === GUARD_FRAMES && this.#guardTotal / GUARD_FRAMES > GUARD_BUDGET_MS) {
        this.#dead = true;
        this.#stop();
        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
        return;
      }
    }

    // Bounded life after the last stroke: clear the layer and sleep
    if (performance.now() - this.#lastStroke > DRY_AFTER_MS) {
      this.ictx.clearRect(0, 0, this.W, this.H);
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
      this.#stop();
      return;
    }

    this.#raf = requestAnimationFrame(this.#tick);
  };
}

if (!customElements.get('rs-signup')) {
  customElements.define('rs-signup', RsSignup);
}
